import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridApi, GridOptions, GridReadyEvent } from 'ag-grid-community';
import {
  CloudAccount,
  CreateCloudEnvironmentDialogData,
  CreateDevOpsActionDialogData,
  DevOpsActionAndParameter,
  ErmDevOpsApiClientService
} from 'erm-sdk';
import * as _ from 'lodash';
import { CreateEnvironmentComponent } from '../create-environment/create-environment.component';
import { CreateDevopsActionDialogComponent } from '../create-devops-action/dialog/create-devops-action-dialog.component';
import dayjs from 'dayjs';

@Component({
  standalone: false,
  templateUrl: './account-list.component.html',
  styleUrls: ['./account-list.component.scss']
})
export class AccountListComponent implements OnInit {

  isLoading = false;
  gridOptions: GridOptions;
  gridApi?: GridApi;
  selectedAccount?: CloudAccount;
  accounts?: CloudAccount[];
  deploymentTypes?: string[];
  environmentVersions?: string[];

  selectedDevOpsAction?: DevOpsActionAndParameter;
  devOpsActions?: DevOpsActionAndParameter[];
  createDevOpsActionDialogData?: CreateDevOpsActionDialogData;

  @ViewChild('agGrid') agGrid!: AgGridAngular;

  constructor(private ermDevOpsApiClient: ErmDevOpsApiClientService, private router: Router, private matdialog: MatDialog) {

    this.gridOptions = {
      columnDefs: this.createColumnDefs(),
      enableCellTextSelection: true,
      suppressCellFocus: true,
      rowSelection: { mode: 'singleRow' },
      onSelectionChanged: event => {
        this.selectedAccount = _.first(event.api.getSelectedNodes().map(x => x.data));
      },
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true
      },
      onGridReady: (params: GridReadyEvent) => {
        this.gridApi = params.api;
      }
    };
  }

  async ngOnInit(): Promise<void> {
    try {

      this.isLoading = true;

      // Get accounts
      const getAccountsResponse = await this.ermDevOpsApiClient.getCloudAccounts();
      this.accounts = getAccountsResponse.body?.cloudAccounts || new Array<CloudAccount>();

      // Get DevOps Actions
      const getDevopsActionsAndParameter = await this.ermDevOpsApiClient.getDevOpsActionsAndParameters();
      this.devOpsActions = getDevopsActionsAndParameter.body?.devOpsActionsAndParameters || new Array<DevOpsActionAndParameter>();

      if (this.devOpsActions) {
        this.devOpsActions = this.devOpsActions.filter(x => x.actionScope == 'Account');
      }

      // Initialize grid
      this.gridApi?.setGridOption('rowData', this.accounts);

    } finally {
      this.isLoading = false;
    }
  }

  private createColumnDefs(): ColDef[] {
    const router = this.router;
    return [
      {
        headerName: 'Customer Id',
        field: 'customerId',
        cellRenderer: (params: any) => {
          const url = router.createUrlTree(['erm/customers', params.value]);
          return `<a href="#${url}">${params.value}</a>`;
        },
      },
      {
        headerName: 'Account Id',
        field: 'accountId',
        cellRenderer: (params: any) => {
          const url = router.createUrlTree(['erm/accounts', params.value]);
          return `<a href="#${url}">${params.value}</a>`;
        },
      },
      { headerName: 'Region', field: 'region' },
      { headerName: 'Description', field: 'description', width: 300 },
      { headerName: 'Maintenance', field: 'inMaintenanceMode' },
      { headerName: 'Enabled', field: 'isEnabled' },
      { headerName: 'Connect Account Id', field: 'connectAccountId', width: 300 },
      {
        headerName: 'Created',
        field: 'creationTime',
        valueFormatter: (params: any) => {
          return dayjs(params.value).format('YYYY-MM-DD HH:mm:ss');
        },
      },
    ];
  }

  async openCreateEnvironment(): Promise<void> {
    try {
      this.isLoading = true;

      if (!this.deploymentTypes) {
        const getDeploymentTypes = await this.ermDevOpsApiClient.getDeploymentTypes();
        this.deploymentTypes = getDeploymentTypes.body?.deploymentTypes || [];
      }
      if (!this.environmentVersions) {
        const getEnvironmentVersions = await this.ermDevOpsApiClient.getEnvironmentVersions();
        this.environmentVersions = getEnvironmentVersions.body?.versions || [];
      }

      this.matdialog.open(CreateEnvironmentComponent, {
        data: {
          accountId: this.selectedAccount?.accountId,
          deploymentTypes: this.deploymentTypes,
          defaultDeploymentType: 'ECS',
          environmentVersions: this.environmentVersions
        } as CreateCloudEnvironmentDialogData
      });

    } finally {
      this.isLoading = false;
    }
  }

  canCreateEnvironment(): boolean {
    return !this.selectedAccount;
  }

  async deleteAccount(): Promise<void> {
    try {
      this.isLoading = true;

      const accountId = this.selectedAccount?.accountId as string;
      await this.ermDevOpsApiClient.deleteCloudAccount(accountId);
      // remove selected item from environment grid
      this.gridOptions['api']?.applyTransaction({
        remove: [this.selectedAccount]
      });
      this.selectedAccount = undefined;
    } finally {
      this.isLoading = false;
    }
  }

  isDeleteAccountDisabled(): boolean {
    // Allow delete only if account is selected from grid and isEnabled is false
    if (this.selectedAccount && !this.selectedAccount.isEnabled) {
      return false;
    }
    else {
      return true;
    }
  }

  async openCreateDevOpsAction(): Promise<void> {
    try {
      this.isLoading = true;
      this.matdialog.open(CreateDevopsActionDialogComponent, {
        data: {
          selectedAccountId: this.selectedAccount?.accountId,
          cloudAccounts: this.accounts,
          devOpsActionsAndParameters: this.devOpsActions,
          selectedDevOpsActionAndParameter: this.selectedDevOpsAction
        } as CreateDevOpsActionDialogData
      });

    } finally {
      this.isLoading = false;
    }
  }

  canCreateDevOpsAction(): boolean {
    return this.isLoading || !this.selectedAccount || !this.selectedDevOpsAction;
  }
}
